import { Immutable } from "immer";
import React from "react";
import { ValuePropsBarBlock } from "tsi-common-react/src/components/ValuePropsBarBlock/index";

import type { PLPHeroBlock as Value } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

type Props = Immutable<{
    value: Value;
}>;

/**
 * Best-effort port of the legacy server-rendered
 * ``sites/tsi-stearns/server/templates/cms/blocks/plp-hero-block.html``.
 *
 * LIVENESS: ``plp_hero`` is absent from every checked-in fixture/test page, and
 * migration ``cms/0030_product_hero.py`` was authored to split it out of the
 * page body (into separate ``gallery_block`` + ``value_prop_bar`` blocks) --
 * though that migration's operations are commented out, so its removal never
 * actually ran. Production occurrence is therefore unverifiable by grep. The
 * block remains in the ``PNPToolbox`` StreamBlock, so reactivated still emits
 * it in the ``PDPToolbox`` / ``PLPToolbox`` body union and a dispatch entry is
 * required for type-checking + a non-crashing render.
 *
 * NOT WIRED (flagged): the legacy block also rendered ``value.gallery`` (the
 * shared PDP-hero ``GalleryBlock``, which needs page-computed SSR
 * ``galleries_data`` -- see ``tsicommon.cms.ssr.gallery``) and the page
 * ``{% modular_configurator %}``. Both are page-dependent and are *already*
 * rendered above the body in the PDP/PLP hero chrome
 * (``<section class="product-hero">``), so faithfully rendering them here would
 * duplicate that chrome. Given the block is not present in any live fixture, we
 * render only the self-contained ``value_props`` and intentionally omit the
 * gallery + configurator rather than thread a second page-dependent SSR
 * side-channel for a dead block.
 */
export const PLPHero = (props: Props): React.JSX.Element => {
    const v = props.value;
    const visibility = v.responsive_visibility?.visibility ?? "";
    return (
        <section
            className={concatClassNames(["pdp-hero", "plp-hero", visibility])}
        >
            <div>
                {v.value_props && (
                    <ValuePropsBarBlock valuePropsData={v.value_props} />
                )}
            </div>
        </section>
    );
};
